/**
 * @file Одна загрузка на авторизованную часть приложения.
 *
 * React хранит состояние панели. Ref хранит запрос с секретами и текущий запуск:
 * они нужны алгоритму, но не должны попадать в данные для отображения.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { clearTransferSnapshots } from '../transfers/snapshots'
import { UploadContext } from './UploadContext'
import { create, finalize, preflight, remove } from './api'
import { runUpload } from './engine'
import { asUploadError, splitFile } from './model'
import type { UploadRequest, UploadState } from './model'
import { segmentInWorker } from './workerAdapter'

const ports = { create, finalize, preflight, remove, segment: segmentInWorker }

export function UploadProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<UploadState | null>(null)
    const requestRef = useRef<UploadRequest | null>(null)
    const active = useRef<{
        controller: AbortController
        // Фаза здесь обновляется сразу: нельзя отменить сборку/сохранение,
        // даже если кнопка на экране ещё не успела скрыться.
        phase: UploadState['phase']
    } | null>(null)
    const mounted = useRef(false)

    useEffect(() => {
        mounted.current = true

        function beforeUnload(event: BeforeUnloadEvent) {
            if (active.current) event.preventDefault()
        }

        window.addEventListener('beforeunload', beforeUnload)

        return () => {
            mounted.current = false
            window.removeEventListener('beforeunload', beforeUnload)
            active.current?.controller.abort()
            active.current = null
            requestRef.current = null
            // Оба Provider живут над dashboard и удаляются вместе (см. App.tsx).
            clearTransferSnapshots()
        }
    }, [])

    function start(request: UploadRequest): boolean {
        if (active.current || !mounted.current) return false

        // Ref обновляется сразу: второе нажатие не запустит ещё один файл,
        // даже если React пока не успел перерисовать кнопку.
        const run = {
            controller: new AbortController(),
            phase: 'checking' as UploadState['phase'],
        }
        active.current = run
        requestRef.current = request
        const id = crypto.randomUUID()

        setState({
            id,
            name: request.file.name,
            size: request.file.size,
            boxId: request.boxId,
            folder: request.folder,
            phase: 'checking',
            segments: (Number.isSafeInteger(request.file.size) &&
            request.file.size >= 0
                ? splitFile(request.file.size)
                : []
            ).map((range) => ({
                ...range,
                uploaded: 0,
                status: 'waiting',
            })),
            error: null,
            cleanupWarning: false,
            retryable: false,
        })

        // Старый запуск после выхода из dashboard уже не может менять панель.
        function update(change: (current: UploadState) => UploadState) {
            if (active.current !== run) return

            setState((current) => (current ? change(current) : current))
        }

        async function execute() {
            try {
                await runUpload(request, id, ports, run.controller.signal, {
                    phase: (phase) => {
                        run.phase = phase
                        update((current) => ({ ...current, phase }))
                    },
                    segment: (index, uploaded, status) =>
                        update((current) => ({
                            ...current,
                            segments: current.segments.map((segment) =>
                                segment.index === index
                                    ? { ...segment, uploaded, status }
                                    : segment,
                            ),
                        })),
                    cleanupWarning: () =>
                        update((current) => ({
                            ...current,
                            cleanupWarning: true,
                        })),
                })
                update((current) => ({ ...current, phase: 'succeeded' }))
                if (active.current === run) requestRef.current = null
            } catch (cause) {
                const error = asUploadError(cause)
                const cancelled = run.controller.signal.aborted
                update((current) => ({
                    ...current,
                    phase: cancelled ? 'cancelled' : 'failed',
                    error: cancelled ? null : error.code,
                    // При неизвестном результате сборки сервер ещё может читать части.
                    retryable: !error.keepParts,
                    segments: current.segments.map((segment) =>
                        ['done', 'failed'].includes(segment.status)
                            ? segment
                            : { ...segment, status: 'stopped' },
                    ),
                }))
            } finally {
                // Engine возвращает управление только после допустимой очистки.
                // До этого слот занят, в том числе после нажатия «Отменить».
                if (active.current === run) active.current = null
            }
        }

        // start отвечает сразу, а execute сам записывает результат или ошибку.
        void execute()
        return true
    }

    function cancel() {
        const run = active.current
        if (!run || !['checking', 'uploading'].includes(run.phase)) return

        run.phase = 'cancelling'
        setState((current) => current && { ...current, phase: 'cancelling' })
        run.controller.abort()
    }

    function retry() {
        // Повтор — новая отправка с нуля, с новым ID и тем же выбранным файлом.
        if (requestRef.current && state?.retryable) start(requestRef.current)
    }

    function dismiss() {
        if (active.current) return

        requestRef.current = null
        setState(null)
    }

    return (
        <UploadContext.Provider
            value={{ state, start, cancel, retry, dismiss }}
        >
            {children}
        </UploadContext.Provider>
    )
}
